// LIBRERIE/IMPORTS
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";

// HOOKS
import { useGetAgentsByAgency } from "../../hooks/useGetAgents";
import { useGetAgencyDetails } from "../../hooks/useGetAgencyDetails";

// COMPONENTI
import SubHeaderDefault from "../Header/subHeader/SubHeaderDefault";
import AgentsSection from "./AgentsSection";
import ServerError from "../Error/ServerError";
import LoadingIndicator from "../Loaders/LoadingIndicator";
import WidgetContacts from "../Widgets/WidgetContacts/WidgetContacts";
import WidgetPartners from '../Widgets/WidgetPartners/WidgetPartners';

// CONFIGURAZIONI
import appConfig from "../../config/appConfig";

interface AboutUsProps {
    agencyId: number;
    showAgents: boolean;
}

const partners = appConfig.content.partners;

const AboutUs: React.FC<AboutUsProps> = ({ agencyId, showAgents }) => {
    const { t } = useTranslation();
    const { agents, loading: agentsLoading, error: agentsError } = useGetAgentsByAgency(agencyId);
    const { agency, loading: agencyLoading, error: agencyError } = useGetAgencyDetails(agencyId);

    if (agencyLoading) return <LoadingIndicator />;
    if (agencyError) return <ServerError />;
    if (agentsLoading && showAgents) return <LoadingIndicator />;
    if (agentsError && showAgents) console.error("Error fetching agents:", agentsError);

    return (
        <>
            <Helmet>
                <title>{t('aboutUsPage.pageTitle')} | {agency?.agencyName || 'Agenzia Immobiliare'}</title>
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
                <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400&family=Lato:wght@300;400&display=swap" rel="stylesheet" />
            </Helmet>

            <SubHeaderDefault title={t('aboutUsPage.pageTitle')} isSeo={true} />

            <div id="page" className="about-us-page">
                <section className="space-ptb">
                    <div className="container">
                        <div className="row align-items-start">
                            <div className="col-12 col-lg-8 mb-4 mb-lg-0">
                                <p dangerouslySetInnerHTML={{ __html: t('aboutUsPage.para1') as string }}></p>
                                <p dangerouslySetInnerHTML={{ __html: t('aboutUsPage.para2') as string }}></p>
                            </div>
                            {/* Colonna Sidebar (Widgets) */}
                            <div id="sidebar" className="col-12 col-lg-4">
                                {showAgents && agentsError && (
                                    <div className="alert alert-danger">{t('aboutUsPage.agentsError', 'Errore nel caricamento degli agenti.')}</div>
                                )}

                                <AgentsSection
                                    showAgents={showAgents}
                                    agents={agents}
                                    agentsLoading={agentsLoading}
                                    agentsError={agentsError}
                                />

                                <WidgetContacts
                                    agencyDataAddress={agency?.address_details?.address ?? ''}
                                    agencyDataCity={agency?.address_details?.city_name ?? ''}
                                    agencyDataComuneCapoluogo={agency?.address_details?.city_name === agency?.address_details?.province_name}
                                    agencyDataProvinciaSigla={agency?.address_details?.sigla ?? ''}
                                    agencyDataEmail={appConfig.agency.agencyEmail}
                                    agencyDataEmailShow={appConfig.ui.agencyWidget.showEmail}
                                    agencyDataPhone1={agency?.phone1 ?? ''}
                                    agencyDataPhone2={agency?.phone2 ?? ''}
                                    agencyDataInternationalPrefix={appConfig.agency.intlPrefix ?? '+39'}
                                    agencyDataMobile={agency?.mobile ?? ''}
                                    agencyDataMobile2={agency?.mobile2 ?? ''}
                                    agencyDataFax={agency?.fax ?? ''}
                                    agencySocialShow={appConfig.ui.agencyWidget.showSocialLinks}
                                    agencyDataWhatsapp={appConfig.socialLinks.whatsapp}
                                    agencyDataFacebook={appConfig.socialLinks.facebook}
                                    agencyDataInstagram={appConfig.socialLinks.instagram}
                                    agencyDataTwitter={appConfig.socialLinks.twitter}
                                    agencyDataLinkedin={appConfig.socialLinks.linkedin}
                                    agencyDataPinterest={appConfig.socialLinks.pinterest}
                                    agencyDataYoutube={appConfig.socialLinks.youtube}
                                />

                                <WidgetPartners partners={partners} />

                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
};
export default AboutUs;